import React, { useState, useEffect } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInbox,
  faEnvelopeOpenText,
  faClock,
  faUser,
  faEye,
  faExclamationTriangle,
  faInfoCircle,
  faBell as faBellSolid,
} from "@fortawesome/free-solid-svg-icons";

/* IMPORT EXTERNAL SIDEBAR */
import TenantSidebar from "../../components/Sidebar/TenantSidebar";
import "./RetrieveTenantMessages.css"; // Importing the polished stylesheet

const RetrieveTenantMessages = () => {
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [lastFetched, setLastFetched] = useState(null);

  const [showSidebar, setShowSidebar] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  /* =========================
      GET USER
  ========================= */
  useEffect(() => {
    const storedUser = localStorage.getItem("user");

    if (storedUser) {
      const user = JSON.parse(storedUser);

      if (user?.email) {
        setUserEmail(user.email);
      }
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /* =========================
      FETCH MESSAGES
  ========================= */
  const handleFetchMessages = async () => {
    if (!userEmail) {
      setStatus("No user email found. Please login again.");
      return;
    }

    try {
      const response = await fetch(
        `https://ikaze-thouse-backend.vercel.app/api/teMessage/messages/${userEmail}`
      );

      const data = await response.json();

      if (data.success && data.messages.length > 0) {
        setMessages(data.messages);
        setStatus("");
      } else {
        setMessages([]);
        setStatus(data.message || "No messages found.");
      }

      setLastFetched(new Date());
    } catch (error) {
      console.error(error);

      setMessages([]);
      setStatus("Error fetching messages.");
      setLastFetched(new Date());
    }
  };

  return (
    <div className="rtm-main-container">
      {/* MOBILE BUTTON */}
      {isMobile && (
        <button
          className="rtm-hamburger-btn"
          onClick={() => setShowSidebar(!showSidebar)}
          aria-label="Toggle navigation menu"
        >
          {showSidebar ? <FaTimes /> : <FaBars />}
        </button>
      )}

      {/* EXTERNAL SIDEBAR */}
      <TenantSidebar
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
        isMobile={isMobile}
      />

      {/* CONTENT */}
      <div className={`rtm-content-wrapper ${isMobile ? "mobile" : "desktop"}`}>
        <div className="rtm-card-container">
          
          {/* WELCOME */}
          <div className="rtm-welcome-banner">
            <FontAwesomeIcon icon={faBellSolid} className="rtm-bounce-icon" />
            <span>Welcome back! Check your latest updates and notifications below.</span>
          </div>

          {/* HEADER */}
          <h2 className="rtm-main-title">
            <FontAwesomeIcon icon={faInbox} className="rtm-title-icon" />
            Retrieve Messages
          </h2>

          {/* INFO */}
          <div className="rtm-info-bar">
            <FontAwesomeIcon icon={faInfoCircle} />
            <span>Click the view button below to load your communication history safely.</span>
          </div>

          {/* BUTTON */}
          <button className="rtm-primary-btn" onClick={handleFetchMessages}>
            <FontAwesomeIcon icon={faEye} />
            View your Messages
          </button>

          {/* STATUS */}
          {status && (
            <div className="rtm-status-alert">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>{status}</span>
            </div>
          )}

          {/* METADATA BLOCK (LAST FETCH & COUNT) */}
          {(lastFetched || messages.length > 0) && (
            <div className="rtm-meta-summary">
              {lastFetched && (
                <span className="rtm-timestamp-meta">
                  Last updated: {lastFetched.toLocaleString()}
                </span>
              )}
              {messages.length > 0 && (
                <span className="rtm-badge-count">
                  {messages.length} Message{messages.length > 1 ? "s" : ""} Found
                </span>
              )}
            </div>
          )}

          {/* MESSAGES FEED */}
          {messages.length > 0 && (
            <div className="rtm-feed-section">
              <h3 className="rtm-feed-heading">
                <FontAwesomeIcon icon={faEnvelopeOpenText} />
                Nitification Inbox
              </h3>

              <div className="rtm-cards-grid">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`rtm-msg-card ${msg.read_status === 0 ? "unread" : "read"}`}
                  >
                    <div className="rtm-card-header">
                      <div className="rtm-guest-identity">
                        <div className="rtm-avatar-placeholder">
                          <FontAwesomeIcon icon={faUser} />
                        </div>
                        <span className="rtm-guest-name">
    <span className="label">To:</span> {msg.guest_name}
</span>
                      </div>
                      {msg.read_status === 0 && <span className="rtm-unread-dot">New</span>}
                    </div>

                    <div className="rtm-msg-body">{msg.message_text}</div>

                    <div className="rtm-card-footer">
                      <FontAwesomeIcon icon={faClock} />
                      <span>{new Date(msg.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* EMPTY STATE */}
          {messages.length === 0 && !status && (
            <div className="rtm-empty-state">
              <div className="rtm-empty-icon">📬</div>
              <p className="rtm-empty-text">
                Your inbox is currently clear. Click <strong>"View your Messages"</strong> to look for updates.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RetrieveTenantMessages;